import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ShieldOff, Fingerprint, FileText, Loader2,
  Copy, Clock, ExternalLink, Layers, Database, Lock, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SHARE_VIEW } from "@/constants/testIds";
import { fetchPublicShare, publicSharePdfUrl } from "@/lib/evidence";
import { toast } from "sonner";

const SEMA_PRIMARY = "#1F3A93";


function copy(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied"); }
  catch { toast.error("Copy failed"); }
}


function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h3
        className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em]"
        style={{ color: SEMA_PRIMARY }}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: SEMA_PRIMARY }} />
        {title}
      </h3>
      <div className="rounded-lg border border-border/60 bg-background/40 p-4">{children}</div>
    </section>
  );
}


function SignatureBadge({ valid, kid }) {
  const ok = !!valid;
  return (
    <Badge
      data-testid={SHARE_VIEW.signatureBadge}
      variant="outline"
      className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${
        ok ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-500"
      }`}
    >
      {ok ? <ShieldCheck className="mr-1 h-3 w-3" /> : <ShieldAlert className="mr-1 h-3 w-3" />}
      {ok ? "Signature verified · Ed25519" : "Signature invalid"}
      {kid && <span className="ml-1.5 text-muted-foreground">· {kid}</span>}
    </Badge>
  );
}


function GonePanel({ status, message, tone = "amber" }) {
  const cfg = tone === "red"
    ? { icon: ShieldOff, color: "text-red-500", ring: "border-red-500/40 bg-red-500/5" }
    : { icon: AlertTriangle, color: "text-amber-500", ring: "border-amber-500/40 bg-amber-500/5" };
  const Icon = cfg.icon;
  const testId = status === "revoked" ? SHARE_VIEW.revoked
    : status === "expired" ? SHARE_VIEW.expired
    : SHARE_VIEW.gone;
  return (
    <div
      data-testid={testId}
      className={`mx-auto mt-24 max-w-lg rounded-2xl border p-10 text-center ${cfg.ring}`}
    >
      <Icon className={`mx-auto mb-4 h-10 w-10 ${cfg.color}`} />
      <h1 className="text-lg font-semibold uppercase tracking-[0.14em]">
        {status === "revoked" ? "Link revoked"
          : status === "expired" ? "Link expired"
          : "Link unavailable"}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">{message}</p>
      <p className="mt-6 text-[10px] text-muted-foreground">
        Please request a fresh link from the person who shared this pack with you.
      </p>
    </div>
  );
}


export default function EvidenceShareView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(null);       // {status, message}
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchPublicShare(token);
        if (!cancelled) setBundle(data);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 410 || e.status === 404) {
          const status = /revoked/i.test(e.message) ? "revoked"
            : /expired/i.test(e.message) ? "expired"
            : "notfound";
          setGone({ status, message: e.message });
        } else {
          setGone({ status: "notfound", message: e.message || "Unavailable" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Private, expiring share links must never be indexed by search engines.
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);


  if (loading) {
    return (
      <div
        data-testid={SHARE_VIEW.loading}
        className="flex min-h-screen items-center justify-center text-sm text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading evidence pack…
      </div>
    );
  }

  if (gone) {
    return <GonePanel status={gone.status} message={gone.message} tone={gone.status === "revoked" ? "red" : "amber"} />;
  }

  if (!bundle) return null;

  const plan = bundle.plan || {};
  const lineage = bundle.lineage || {};
  const rbac = bundle.rbac || {};

  const pdfHref = publicSharePdfUrl(token);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={SHARE_VIEW.root}>
      <header
        className="border-b border-border/40 bg-card/40 backdrop-blur"
        style={{ background: "rgba(31,58,147,0.03)" }}
      >
        <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-6 py-5">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: SEMA_PRIMARY }}>
            <ShieldCheck className="h-4 w-4" />
            Sema · Shared Evidence Pack
          </div>
          <SignatureBadge valid={bundle.signature_valid} kid={bundle.signing_kid} />
          <a
            href={pdfHref}
            className="ml-auto inline-flex h-9 items-center gap-2 rounded-full px-4 text-xs font-medium text-white hover:opacity-95"
            style={{ background: SEMA_PRIMARY }}
            data-testid={SHARE_VIEW.exportPdf}
          >
            <FileText className="h-3.5 w-3.5" /> Export PDF
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Question
        </p>
        <h1
          data-testid={SHARE_VIEW.question}
          className="mt-2 text-2xl font-semibold leading-snug sm:text-3xl"
        >
          {bundle.question || "(no question captured)"}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>Generated {bundle.generated_at}</span>
          <span className="mx-1">·</span>
          <span data-testid={SHARE_VIEW.answerId} className="font-mono text-[10px]">
            {bundle.answer_id}
          </span>
          <span className="mx-1">·</span>
          <span>Graph <span className="font-mono">{bundle.graph_version}</span></span>
        </div>

        {bundle.share_expires_at && (
          <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <Clock className="h-3 w-3" />
            This link expires {new Date(bundle.share_expires_at).toLocaleString()}
          </div>
        )}

        <Section title="Explanation">
          <p className="text-sm leading-relaxed">
            {bundle.explanation || "(no explanation)"}
          </p>
        </Section>

        <Section title="Generated SQL">
          <div className="flex items-start justify-between gap-2">
            <pre
              data-testid={SHARE_VIEW.sql}
              className="max-h-64 flex-1 overflow-auto rounded bg-background/60 p-3 font-mono text-[11px]"
            >
              {bundle.sql || "(no SQL)"}
            </pre>
            {bundle.sql && (
              <button
                onClick={() => copy(bundle.sql)}
                className="rounded-md border border-border/60 p-1.5 text-muted-foreground hover:text-foreground"
                title="Copy SQL"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </Section>

        <Section title="Tables & lineage">
          {(lineage.tables || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No tables recorded.</p>
          ) : (
            <ul className="space-y-2">
              {(lineage.tables || []).map((t) => (
                <li key={t.table_fqn} className="rounded-md border border-border/40 p-2">
                  <div className="flex items-center gap-2 text-xs">
                    <Database className="h-3.5 w-3.5" style={{ color: SEMA_PRIMARY }} />
                    <span className="font-mono">{t.table_fqn}</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      {t.row_count ?? "?"} rows
                    </span>
                  </div>
                  <div className="mt-1 pl-5 text-[11px] text-muted-foreground">
                    {(t.columns || []).slice(0, 12).map((c) => c.name).join(", ") || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(lineage.join_edges || []).length > 0 && (
            <div className="mt-3 border-t border-border/40 pt-3">
              <div className="mb-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                <Layers className="h-3 w-3" /> join lineage
              </div>
              <ul className="space-y-0.5">
                {(lineage.join_edges || []).map((e, i) => (
                  <li key={i} className="text-[11px] font-mono text-muted-foreground">
                    {e.from_fqn}.{e.from_column} → {e.to_fqn}.{e.to_column}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Section title="Access context">
          <div className="grid grid-cols-[7rem_1fr] gap-2 text-xs">
            <div className="text-muted-foreground">User</div>
            <div>{rbac.user_email || rbac.user_id || "system"}</div>
            <div className="text-muted-foreground">Role</div>
            <div>{rbac.role || "—"}</div>
            <div className="text-muted-foreground">Masked columns</div>
            <div>
              {(rbac.masked_columns || []).length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {rbac.masked_columns.map((c) => (
                    <Badge key={c} variant="outline" className="rounded-full text-[10px]">
                      <Lock className="mr-1 h-2.5 w-2.5" />
                      {c}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">none</span>
              )}
            </div>
          </div>
        </Section>

        <Section title="Signature">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <SignatureBadge valid={bundle.signature_valid} kid={bundle.signing_kid} />
            <span className="font-mono text-[10px] text-muted-foreground">
              <Fingerprint className="inline h-3 w-3" /> {bundle.signing_kid}
            </span>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            This pack is signed with Ed25519 over the canonical JSON of the bundle.
            The signature is verified server-side each time the link is opened —
            the badge above reflects the live result.
          </p>
        </Section>

        <footer className="mt-12 flex items-center justify-between border-t border-border/40 pt-6 text-[10px] text-muted-foreground">
          <a
            href="/"
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" /> Powered by Sema
          </a>
          <span>Read-only shared view · no login required</span>
        </footer>
      </main>
    </div>
  );
}
